import { useEffect, useState } from 'react';
import { metaApi } from '../api';
import { LANGUAGES, savePrefs, usePrefs, type Prefs } from '../prefs';

/** Kijkvoorkeuren voor dit profiel: talen, ondertitels, overslaan, volume en trailers. */
export type PlaybackGroup = 'audio' | 'subs' | 'substyle' | 'playback' | 'trakt' | 'screensaver' | 'netflix' | 'titlepage';
const ALL_GROUPS: PlaybackGroup[] = ['audio', 'subs', 'substyle', 'playback', 'trakt', 'screensaver'];

export function PlaybackSettings({ groups = ALL_GROUPS, title = 'Kijken', intro = true }: { groups?: PlaybackGroup[]; title?: string; intro?: boolean }) {
  const has = (g: PlaybackGroup) => groups.includes(g);
  const p = usePrefs();
  const [status, setStatus] = useState<string | null>(null);
  const [trakt, setTrakt] = useState<{ linked: boolean; username: string | null } | null>(null);
  useEffect(() => {
    metaApi.trakt().then(setTrakt).catch(() => setTrakt(null));
  }, []);

  const save = (patch: Partial<Prefs>) => {
    setStatus('Opslaan…');
    savePrefs(patch).then(() => setStatus('Opgeslagen')).catch(() => setStatus('Opslaan mislukt'));
  };
  const style = (patch: Partial<Prefs['subStyle']>) => save({ subStyle: { ...p.subStyle, ...patch } });

  const audio1 = p.audioLangs[0] ?? '';
  const audio2 = p.audioLangs[1] ?? '';
  const sub1 = p.subLangs[0] ?? '';
  const sub2 = p.subLangs[1] ?? '';
  const langs = (a: string, b: string) => [a, b].filter((x, i, l) => x && l.indexOf(x) === i);

  return (
    <section className="settings-block" aria-label={title}>
      <h2>{title} {status && <span className="subtle pb-status" role="status">{status}</span>}</h2>
      {intro && <p className="subtle hint">Voor dit profiel, op elk apparaat. Kies je in de player zelf een ander spoor, dan onthoudt tumedia dat voor die titel.</p>}

      <div className="pb-grid">
        {has('audio') && (
        <fieldset className="pb-group">
          <legend>Audio</legend>
          <label className="ed-field">Voorkeurstaal
            <select value={audio1} onChange={(e) => save({ audioLangs: langs(e.target.value, audio2) })}>
              <option value="">Geen voorkeur (standaardspoor)</option>
              {LANGUAGES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
          </label>
          {audio1 && (
            <label className="ed-field">Anders
              <select value={audio2} onChange={(e) => save({ audioLangs: langs(audio1, e.target.value) })}>
                <option value="">Standaardspoor</option>
                {LANGUAGES.filter(([c]) => c !== audio1).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </select>
            </label>
          )}
        </fieldset>
        )}

        {has('subs') && (
        <fieldset className="pb-group">
          <legend>Ondertitels</legend>
          <label className="ed-field">Wanneer
            <select value={p.subMode} onChange={(e) => save({ subMode: e.target.value as Prefs['subMode'] })}>
              <option value="auto">Als ik de gesproken taal niet versta</option>
              <option value="always">Altijd</option>
              <option value="forced">Alleen vertaling van buitenlandse stukjes</option>
              <option value="off">Nooit</option>
            </select>
          </label>
          {p.subMode !== 'off' && (
            <>
              <label className="ed-field">Taal
                <select value={sub1} onChange={(e) => save({ subLangs: langs(e.target.value, sub2) })}>
                  {LANGUAGES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
              </label>
              <label className="ed-field">Anders
                <select value={sub2} onChange={(e) => save({ subLangs: langs(sub1, e.target.value) })}>
                  <option value="">Geen</option>
                  {LANGUAGES.filter(([c]) => c !== sub1).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
              </label>
            </>
          )}
          <span className="subtle hint">"Versta ik": de gesproken taal staat in je ondertiteltalen. Engelse film + Nederlands ingesteld → Nederlandse ondertitels.</span>
        </fieldset>
        )}

        {has('substyle') && (
        <fieldset className="pb-group">
          <legend>Opmaak ondertitels</legend>
          <Chips label="Grootte" value={p.subStyle.size} options={[['small', 'Klein'], ['normal', 'Normaal'], ['large', 'Groot'], ['xl', 'Extra groot']]} onChange={(v) => style({ size: v })} />
          <Chips label="Kleur" value={p.subStyle.color} options={[['white', 'Wit'], ['yellow', 'Geel']]} onChange={(v) => style({ color: v })} />
          <Chips label="Achtergrond" value={p.subStyle.background} options={[['shadow', 'Schaduw'], ['box', 'Blok'], ['none', 'Geen']]} onChange={(v) => style({ background: v })} />
          <Chips label="Plaats" value={p.subStyle.position} options={[['bottom', 'Onderaan'], ['raised', 'Iets hoger']]} onChange={(v) => style({ position: v })} />
          <div className={`pb-sub-sample sub-${p.subStyle.size} sub-${p.subStyle.color} sub-bg-${p.subStyle.background}`} aria-hidden="true">
            <span>Zo zien je ondertitels eruit.</span>
          </div>
          <span className="subtle hint">Geldt voor tekstondertitels (SRT). Beeld-ondertitels (PGS) brandt Plex in zoals ze zijn.</span>
        </fieldset>
        )}

        {has('playback') && (
        <fieldset className="pb-group">
          <legend>Afspelen</legend>
          <Toggle label="Intro en samenvatting automatisch overslaan" checked={p.autoSkipIntro} onChange={(v) => save({ autoSkipIntro: v })} />
          <Toggle label="Leeftijdskaart bij de start (leeftijd, ouderinfo, beeld en geluid)" checked={p.ratingCard} onChange={(v) => save({ ratingCard: v })} />
          <Toggle label="Volgende aflevering automatisch starten" checked={p.autoplayNext} onChange={(v) => save({ autoplayNext: v })} />
          {p.autoplayNext && (
            <Chips label="Bij de aftiteling" value={String(p.nextCountdown)} options={[['0', 'Meteen'], ['5', 'Na 5 s'], ['10', 'Na 10 s'], ['20', 'Na 20 s']]} onChange={(v) => save({ nextCountdown: Number(v) })} />
          )}
          <Chips label="Volume" value={p.loudness} options={[['off', 'Origineel'], ['normal', 'Gelijkmatig'], ['night', 'Nachtmodus']]} onChange={(v) => save({ loudness: v })} />
          <span className="subtle hint">Gelijkmatig: harde stukken zachter, dialogen beter verstaanbaar. Werkt direct in de player, zonder dat Plex iets hoeft te analyseren.</span>
          <Toggle label="Trailers in de spotlight (zonder geluid)" checked={p.spotlightTrailers} onChange={(v) => save({ spotlightTrailers: v })} />
          <Toggle label="Spoilers verbergen (beelden en tekst van afleveringen die je nog niet zag)" checked={p.hideSpoilers} onChange={(v) => save({ hideSpoilers: v })} />
        </fieldset>
        )}

        {has('playback') && (
        <fieldset className="pb-group">
          <legend>Bioscoop en theme-muziek</legend>
          <Chips label="Bioscoopmodus" value={String(p.cinemaTrailers ?? 0)} options={[['0', 'Uit'], ['1', '1 trailer'], ['2', '2 trailers'], ['3', '3 trailers']]} onChange={(v) => save({ cinemaTrailers: Number(v) })} />
          <span className="subtle hint">Vóór een film eerst trailers van films uit je eigen bibliotheek die je nog niet zag. Overslaan kan altijd. Niet bij hervatten en niet bij samen kijken.</span>
          <Chips label="Theme-muziek" value={p.themeMusic ?? 'off'} options={[['off', 'Uit'], ['shows', 'Series'], ['all', 'Series en films']]} onChange={(v) => save({ themeMusic: v })} />
          <Chips label="Volume" value={String(p.themeVolume ?? 35)} options={[['15', 'Zacht'], ['35', 'Normaal'], ['60', 'Hard']]} onChange={(v) => save({ themeVolume: Number(v) })} />
          <span className="subtle hint">Op de pagina van een serie speelt het themanummer van Plex zacht af. Films alleen als er een theme.mp3 naast het bestand staat. Er wordt niets gedownload.</span>
        </fieldset>
        )}

        {has('trakt') && (
        <fieldset className="pb-group">
          <legend>Trakt</legend>
          {trakt?.linked ? (
            <>
              <Toggle label={`Wat ik kijk bijwerken op Trakt${trakt.username ? ` (${trakt.username})` : ''}`} checked={p.traktScrobble} onChange={(v) => save({ traktScrobble: v })} />
              <span className="subtle hint">Films en afleveringen komen vanzelf in je Trakt-geschiedenis; tijdens het kijken zie je op Trakt "Nu aan het kijken".</span>
            </>
          ) : (
            <span className="subtle hint">Koppel Trakt bij Koppelingen om bij te houden wat je kijkt en titels te beoordelen.</span>
          )}
        </fieldset>
        )}

        {has('titlepage') && (
        <fieldset className="pb-group">
          <legend>Titelpagina</legend>
          <Toggle label="Beoordelingen tonen (IMDb, Rotten Tomatoes, Trakt…)" checked={p.showRatings} onChange={(v) => save({ showRatings: v })} />
          <Toggle label="Ouderinfo tonen (geweld, seks, grof taalgebruik…)" checked={p.showParentsGuide} onChange={(v) => save({ showParentsGuide: v })} />
          <Toggle label="Recensies tonen" checked={p.showReviews} onChange={(v) => save({ showReviews: v })} />
          <span className="subtle hint">Geldt voor dit profiel, op het web en in de apps.</span>
        </fieldset>
        )}

        {has('netflix') && (
        <fieldset className="pb-group">
          <legend>Netflix-look</legend>
          <Toggle label="Trailers in opengeklapte kaarten" checked={p.cardTrailers} onChange={(v) => save({ cardTrailers: v })} />
          <Toggle label="Trailer in billboard en spotlight" checked={p.spotlightTrailers} onChange={(v) => save({ spotlightTrailers: v })} />
          <Toggle label="Geluid bij trailers" checked={p.trailerSound} onChange={(v) => save({ trailerSound: v })} />
          <Toggle label="Beschrijving onder de rij" checked={p.rowInfo} onChange={(v) => save({ rowInfo: v })} />
          <Toggle label="OLED-zwart (puur zwart, beeld vervaagt in de achtergrond)" checked={p.oled} onChange={(v) => save({ oled: v })} />
          <span className="subtle hint">Rijen met de weergave "Netflix" klappen open als je erop staat; een billboard bovenaan volgt je selectie. Weergave kies je per widget in de startscherm-editor.</span>
        </fieldset>
        )}

        {has('screensaver') && (
        <fieldset className="pb-group">
          <legend>Screensaver</legend>
          <label className="ed-field">Na hoeveel tijd
            <select value={p.screensaverMinutes} onChange={(e) => save({ screensaverMinutes: Number(e.target.value) })}>
              <option value={0}>Uit</option>
              <option value={2}>Na 2 minuten</option>
              <option value={5}>Na 5 minuten</option>
              <option value={10}>Na 10 minuten</option>
              <option value={20}>Na 20 minuten</option>
              <option value={30}>Na 30 minuten</option>
            </select>
          </label>
          <label className="ed-field">Titels
            <select value={p.ssSource ?? 'mix'} onChange={(e) => save({ ssSource: e.target.value as 'library' | 'trending' | 'mix' })}>
              <option value="mix">Bibliotheek en trending door elkaar</option>
              <option value="library">Alleen uit je bibliotheek</option>
              <option value="trending">Alleen trending deze week</option>
            </select>
          </label>
          <Toggle label="Titel met logo, genres en beschrijving" checked={p.ssParts?.info !== false} onChange={(v) => save({ ssParts: { ...(p.ssParts ?? { info: true, clock: true, upcoming: true, countdown: true }), info: v } })} />
          <Toggle label="Klok en datum" checked={p.ssParts?.clock !== false} onChange={(v) => save({ ssParts: { ...(p.ssParts ?? { info: true, clock: true, upcoming: true, countdown: true }), clock: v } })} />
          <Toggle label="Binnenkort: deze week en je volgende afleveringen" checked={p.ssParts?.upcoming !== false} onChange={(v) => save({ ssParts: { ...(p.ssParts ?? { info: true, clock: true, upcoming: true, countdown: true }), upcoming: v } })} />
          <Toggle label="Aftellen tot de volgende aflevering" checked={p.ssParts?.countdown !== false} onChange={(v) => save({ ssParts: { ...(p.ssParts ?? { info: true, clock: true, upcoming: true, countdown: true }), countdown: v } })} />
          <span className="subtle hint">Elke toets of beweging stopt hem. "Binnenkort" komt uit je kalender (series in je bibliotheek en op Trakt).</span>
        </fieldset>
        )}
      </div>
    </section>
  );
}

function Chips<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="ed-field">
      <span>{label}</span>
      <div className="chip-wrap" role="radiogroup" aria-label={label}>
        {options.map(([id, text]) => (
          <button key={id} role="radio" aria-checked={value === id} aria-pressed={value === id} className="chip" onClick={() => onChange(id)}>{text}</button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="studio-toggle">
      <span>{label}</span>
      <span className="toggle"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /></span>
    </label>
  );
}
